import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getAuth, User } from "firebase/auth";
import {
  getFirestore,
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  getDocs,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { AppDispatch } from "./store";

/* ═══════════════════════════════════════════════════════════
   TYPES
═══════════════════════════════════════════════════════════ */

export interface GlamourDeliveryZone {
  country: string;
  city: string;
  neighborhood: string;
  region: string;
  zipCode: string;
  latitude: number | null;
  longitude: number | null;
  deliveryFee: number;
  deliveryTime: number; // estimated days
  fulfilledBy: {
    isFBA: boolean; // fulfilled by Glamour
    isFBS: boolean; // fulfilled by Seller
  };
}

export interface GlamourBrand {
  brandId: string;   // == Firestore doc ID
  userId: string;    // owner UID

  // Identity
  name?: string;
  description?: string;
  tagline?: string;
  website?: string;
  email?: string;
  phone?: string | null;
  image?: string | null;
  coverImage?: string | null;

  // Classification
  categoryId?: number | null;
  subcategoryId?: number | null;
  businessType?: number | null;
  category?: string | null;

  // Address
  address?: {
    street?: string;
    neighborhood?: string;
    city?: string;
    county?: string;
    country?: string;
    latitude?: number | null;
    longitude?: number | null;
  };

  // Delivery
  delivery?: GlamourDeliveryZone[];
  fulfilledBy?: {
    isFBA: boolean;
    isFBS: boolean;
  };

  // Social
  instagram?: string | null;
  tiktok?: string | null;
  facebook?: string | null;

  // Dates
  joinDate: string;
  createdAt?: any;
  updatedAt?: any;

  // Status flags
  isPublished: boolean;
  isFlagged: boolean;
  isComplete: boolean;
  inReview: boolean;
  isDraft: boolean;
  deleted?: boolean;
  isSuspended?: boolean;
  isVerified?: boolean;

  // Stats (denormalised — updated by cloud functions or on fetch)
  totalProducts?: number;
  totalOrders?: number;
  totalRevenue?: number;
  avgRating?: number;

  [key: string]: any;
}

interface GlamourBrandsState {
  brands: GlamourBrand[];
  currentBrand: GlamourBrand | null;
  loading: boolean;
  error: string | null;
}

const initialState: GlamourBrandsState = {
  brands: [],
  currentBrand: null,
  loading: false,
  error: null,
};

/* ═══════════════════════════════════════════════════════════
   FIREBASE INSTANCES
═══════════════════════════════════════════════════════════ */

const db      = getFirestore();
const auth    = getAuth();
const storage = getStorage();

const COLLECTION = "brands"; // 🔑 Glamour collection name

/* ═══════════════════════════════════════════════════════════
   PARSER  — raw Firestore doc → GlamourBrand
═══════════════════════════════════════════════════════════ */

const parseBrand = (id: string, raw: Record<string, any>): GlamourBrand => {
  const a = raw.address || {};

  return {
    brandId: id,
    userId:  raw.userId ?? "",

    name:        raw.name        ?? "",
    description: raw.description ?? "",
    tagline:     raw.tagline     ?? "",
    website:     raw.website     ?? "",
    email:       raw.email       ?? "",
    phone:       raw.phone       ?? "",
    image:       raw.image       ?? "",
    coverImage:  raw.coverImage  ?? "",

    categoryId:    raw.categoryId    ?? null,
    subcategoryId: raw.subcategoryId ?? null,
    businessType:  raw.businessType  ?? null,
    category:      raw.category      ?? "",

    address: {
      street:       a.street       ?? "",
      neighborhood: a.neighborhood ?? "",
      city:         a.city         ?? "",
      county:       a.county       ?? "",
      country:      a.country      ?? "",
      latitude:
        typeof a.latitude === "string" ? Number(a.latitude) : a.latitude ?? null,
      longitude:
        typeof a.longitude === "string" ? Number(a.longitude) : a.longitude ?? null,
    },

    delivery: Array.isArray(raw.delivery)
      ? raw.delivery.map((z: any) => ({
          country:      z.country      ?? "",
          city:         z.city         ?? "",
          neighborhood: z.neighborhood ?? "",
          region:       z.region       ?? "",
          zipCode:      z.zipCode      ?? "",
          latitude:     z.latitude     ?? null,
          longitude:    z.longitude    ?? null,
          deliveryFee:  z.deliveryFee  ?? 0,
          deliveryTime: z.deliveryTime ?? 3,
          fulfilledBy: {
            isFBA: z.fulfilledBy?.isFBA ?? false,
            isFBS: z.fulfilledBy?.isFBS ?? true,
          },
        }))
      : [],

    fulfilledBy: {
      isFBA: raw.fulfilledBy?.isFBA ?? false,
      isFBS: raw.fulfilledBy?.isFBS ?? true,
    },

    instagram: raw.instagram ?? "",
    tiktok:    raw.tiktok    ?? "",
    facebook:  raw.facebook  ?? "",

    joinDate:  raw.joinDate  ?? new Date().toISOString(),
    createdAt: raw.createdAt?.toMillis?.() ?? null,
    updatedAt: raw.updatedAt?.toMillis?.() ?? null,

    isPublished:  raw.isPublished  ?? false,
    isFlagged:    raw.isFlagged    ?? false,
    isComplete:   raw.isComplete   ?? false,
    inReview:     raw.inReview     ?? false,
    isDraft:      raw.isDraft      ?? true,
    deleted:      raw.deleted      ?? false,
    isSuspended:  raw.isSuspended  ?? false,
    isVerified:   raw.isVerified   ?? false,

    totalProducts: raw.totalProducts ?? 0,
    totalOrders:   raw.totalOrders   ?? 0,
    totalRevenue:  raw.totalRevenue  ?? 0,
    avgRating:     raw.avgRating     ?? 0,
  };
};

/* ═══════════════════════════════════════════════════════════
   HELPER — remove undefined keys before writing to Firestore
═══════════════════════════════════════════════════════════ */

const clean = (obj: any): any => {
  const out: any = {};
  Object.keys(obj).forEach((k) => {
    if (obj[k] !== undefined) out[k] = obj[k];
  });
  return out;
};

/* ═══════════════════════════════════════════════════════════
   NEAREST DELIVERY ZONE
═══════════════════════════════════════════════════════════ */

const getDistanceKm = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R    = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const getNearestDeliveryZone = createAsyncThunk<
  { brandId: string; deliveryFee: number; deliveryTime: number; matchedZone: GlamourDeliveryZone | null },
  { brandId: string; userLatitude: number; userLongitude: number }
>(
  "glamourBrands/nearestDeliveryZone",
  async ({ brandId, userLatitude, userLongitude }) => {
    const snap = await getDoc(doc(db, COLLECTION, brandId));

    if (!snap.exists()) {
      return { brandId, deliveryFee: 0, deliveryTime: 0, matchedZone: null };
    }

    const zones: GlamourDeliveryZone[] = snap.data().delivery ?? [];

    let nearestZone: GlamourDeliveryZone | null = null;
    let shortestDistance = Infinity;

    for (const zone of zones) {
      if (zone.latitude == null || zone.longitude == null) continue;

      const dist = getDistanceKm(
        userLatitude,
        userLongitude,
        zone.latitude,
        zone.longitude
      );

      if (dist <= 50 && dist < shortestDistance) {
        shortestDistance = dist;
        nearestZone = zone;
      }
    }

    return {
      brandId,
      deliveryFee:  nearestZone?.deliveryFee  ?? 0,
      deliveryTime: nearestZone?.deliveryTime ?? 0,
      matchedZone:  nearestZone,
    };
  }
);

/* ═══════════════════════════════════════════════════════════
   UPSERT BRAND  (create or update)
═══════════════════════════════════════════════════════════ */

export const upsertGlamourBrand = createAsyncThunk<
  GlamourBrand,
  { brandId?: string; data: Partial<GlamourBrand> }
>(
  "glamourBrands/upsert",
  async ({ brandId, data }, { rejectWithValue }) => {
    try {
      const user = auth.currentUser as User | null;
      if (!user) throw new Error("User not authenticated");

      const colRef = collection(db, COLLECTION);

      const normalized = clean({
        ...data,
        address: data.address
          ? clean({
              ...data.address,
              latitude:
                typeof data.address.latitude === "string"
                  ? Number(data.address.latitude)
                  : data.address.latitude,
              longitude:
                typeof data.address.longitude === "string"
                  ? Number(data.address.longitude)
                  : data.address.longitude,
            })
          : undefined,
      });

      const updateBase = { ...normalized, updatedAt: serverTimestamp() };

      /* ── UPDATE ── */
      if (brandId) {
        const refDoc = doc(colRef, brandId);
        await updateDoc(refDoc, updateBase);
        const snap = await getDoc(refDoc);
        return parseBrand(snap.id, snap.data() || {});
      }

      /* ── CREATE ── */
      const createBase = {
        ...updateBase,
        userId:      user.uid,
        joinDate:    new Date().toISOString(),
        isPublished: false,
        isFlagged:   false,
        isComplete:  false,
        inReview:    false,
        isDraft:     true,
        isSuspended: false,
        isVerified:  false,
        createdAt:   serverTimestamp(),
      };

      const newRef = await addDoc(colRef, createBase);
      await updateDoc(newRef, { brandId: newRef.id });

      const snap = await getDoc(newRef);
      return parseBrand(newRef.id, snap.data() || {});
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to upsert brand");
    }
  }
);

/* ═══════════════════════════════════════════════════════════
   UPLOAD BRAND LOGO
═══════════════════════════════════════════════════════════ */

export const uploadGlamourBrandLogo = createAsyncThunk<
  string,
  { brandId: string; file: File; type?: "logo" | "cover" }
>(
  "glamourBrands/uploadLogo",
  async ({ brandId, file, type = "logo" }, { dispatch }) => {
    const path    = `brands/${brandId}/${type}/${Date.now()}_${file.name}`;
    const fileRef = ref(storage, path);

    await uploadBytes(fileRef, file);
    const url = await getDownloadURL(fileRef);

    const field = type === "cover" ? "coverImage" : "image";
    await dispatch(upsertGlamourBrand({ brandId, data: { [field]: url } }));

    return url;
  }
);

/* ═══════════════════════════════════════════════════════════
   FETCH ALL BRANDS  (admin / discovery)
═══════════════════════════════════════════════════════════ */

export const fetchAllGlamourBrands = createAsyncThunk<GlamourBrand[]>(
  "glamourBrands/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const snap = await getDocs(collection(db, COLLECTION));
      return snap.docs.map((d) => parseBrand(d.id, d.data()));
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch brands");
    }
  }
);

/* ═══════════════════════════════════════════════════════════
   FETCH PUBLISHED BRANDS  (storefront)
═══════════════════════════════════════════════════════════ */

export const fetchPublishedGlamourBrands = createAsyncThunk<GlamourBrand[]>(
  "glamourBrands/fetchPublished",
  async (_, { rejectWithValue }) => {
    try {
      const q    = query(collection(db, COLLECTION), where("isPublished", "==", true));
      const snap = await getDocs(q);
      return snap.docs.map((d) => parseBrand(d.id, d.data()));
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch published brands");
    }
  }
);

/* ═══════════════════════════════════════════════════════════
   LISTEN TO CURRENT USER BRANDS  (real-time)
═══════════════════════════════════════════════════════════ */

let userBrandsUnsub: (() => void) | null = null;

export const listenToUserGlamourBrands =
  (userId: string) => (dispatch: AppDispatch) => {
    if (userBrandsUnsub) userBrandsUnsub();

    const q = query(collection(db, COLLECTION), where("userId", "==", userId));

    userBrandsUnsub = onSnapshot(q, (snap) => {
      dispatch(setBrands(snap.docs.map((d) => parseBrand(d.id, d.data()))));
    });

    return () => {
      userBrandsUnsub?.();
      userBrandsUnsub = null;
    };
  };

/* ═══════════════════════════════════════════════════════════
   LISTEN TO SINGLE BRAND  (real-time, dashboard)
═══════════════════════════════════════════════════════════ */

export const listenToGlamourBrand =
  (brandId: string) => (dispatch: AppDispatch) => {
    const docRef = doc(db, COLLECTION, brandId);

    const unsubscribe = onSnapshot(
      docRef,
      (snap) => {
        if (snap.exists()) {
          dispatch(
            glamourBrandsSlice.actions.setCurrentBrand(
              parseBrand(snap.id, snap.data())
            )
          );
        } else {
          dispatch(glamourBrandsSlice.actions.setCurrentBrand(null));
        }
      },
      (error) => {
        dispatch(glamourBrandsSlice.actions.setError(error.message));
      }
    );

    return unsubscribe;
  };

/* ═══════════════════════════════════════════════════════════
   SOFT DELETE BRAND
═══════════════════════════════════════════════════════════ */

export const softDeleteGlamourBrand = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>(
  "glamourBrands/softDelete",
  async (brandId, { rejectWithValue }) => {
    try {
      await updateDoc(doc(db, COLLECTION, brandId), {
        deleted:     true,
        isPublished: false,
        updatedAt:   serverTimestamp(),
      });
      return brandId;
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to delete brand");
    }
  }
);

/* ═══════════════════════════════════════════════════════════
   TOGGLE PUBLISH
═══════════════════════════════════════════════════════════ */

export const toggleGlamourBrandPublish = createAsyncThunk<
  { brandId: string; isPublished: boolean },
  { brandId: string; isPublished: boolean },
  { rejectValue: string }
>(
  "glamourBrands/togglePublish",
  async ({ brandId, isPublished }, { rejectWithValue }) => {
    try {
      await updateDoc(doc(db, COLLECTION, brandId), {
        isPublished,
        updatedAt: serverTimestamp(),
      });
      return { brandId, isPublished };
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to toggle publish");
    }
  }
);

/* ═══════════════════════════════════════════════════════════
   SLICE
═══════════════════════════════════════════════════════════ */

const glamourBrandsSlice = createSlice({
  name: "glamourBrands",
  initialState,
  reducers: {
    setBrands: (state, action: PayloadAction<GlamourBrand[]>) => {
      state.brands = action.payload;
    },
    setCurrentBrand: (state, action: PayloadAction<GlamourBrand | null>) => {
      state.currentBrand = action.payload;
    },
    clearGlamourBrands: (state) => {
      state.brands       = [];
      state.currentBrand = null;
      state.error        = null;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (b) => {
    /* ── upsert ── */
    b.addCase(upsertGlamourBrand.fulfilled, (state, action) => {
      const idx = state.brands.findIndex((p) => p.brandId === action.payload.brandId);
      if (idx !== -1) state.brands[idx] = action.payload;
      else state.brands.push(action.payload);
      state.currentBrand = action.payload;
      state.loading      = false;
    });

    /* ── fetchAll ── */
    b.addCase(fetchAllGlamourBrands.fulfilled, (state, action) => {
      state.brands  = action.payload;
      state.loading = false;
    });

    /* ── fetchPublished ── */
    b.addCase(fetchPublishedGlamourBrands.fulfilled, (state, action) => {
      state.brands  = action.payload;
      state.loading = false;
    });

    /* ── softDelete ── */
    b.addCase(softDeleteGlamourBrand.fulfilled, (state, action) => {
      state.brands       = state.brands.filter((b) => b.brandId !== action.payload);
      state.currentBrand = null;
      state.loading      = false;
    });

    /* ── togglePublish ── */
    b.addCase(toggleGlamourBrandPublish.fulfilled, (state, action) => {
      const idx = state.brands.findIndex((b) => b.brandId === action.payload.brandId);
      if (idx !== -1) state.brands[idx].isPublished = action.payload.isPublished;
      if (state.currentBrand?.brandId === action.payload.brandId) {
        state.currentBrand.isPublished = action.payload.isPublished;
      }
    });

    /* ── global pending / rejected matcher ── */
    b.addMatcher(
      (a) => a.type.startsWith("glamourBrands/") && a.type.endsWith("/pending"),
      (state) => {
        state.loading = true;
        state.error   = null;
      }
    );
    b.addMatcher(
      (a) => a.type.startsWith("glamourBrands/") && a.type.endsWith("/rejected"),
      (state, a: any) => {
        state.loading = false;
        state.error   = a.payload ?? a.error?.message ?? "Something went wrong";
      }
    );
  },
});

export const { setBrands, setCurrentBrand, clearGlamourBrands, setError } =
  glamourBrandsSlice.actions;

export default glamourBrandsSlice.reducer;